import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { Modal, Steps, Button, Tag, Typography, Space } from "antd";
import "@styles/styles-SandboxTutorial.css";

import type { GridWidgetType } from "../types";
import { getWidgetTemplates } from "../widgetRegistry";
import { isLocked } from "../AddWidgetModal/TemplatePicker";
import { resolveTemplate } from "../Utils/resolveTemplate";

import img_overview from "./img/sandbox-overview.png";
import img_addBtn from "./img/sandbox-add-widget-button.png";
import img_widgetPicker from "./img/sandbox-widget-picker.png";
import img_configPlot from "./img/sandbox-config-plot.png";
import img_configTable from "./img/sandbox-config-table.png";
import img_config2dTable from "./img/sandbox-2d-table.png";
import img_configStatTile from "./img/sandbox-stat-tile.png";
import img_configGauge from "./img/sandbox-gauge-chart.png";
import img_configPie from "./img/sandbox-pi-chart.png";
import img_configRadar from "./img/sandbox-radar-chart.png";
import img_configTitle from "./img/sandbox-title-overview.png";
import img_configBodyMap from "./img/sandbox-bodymap.png";
import img_configPlayerNotes from "./img/sandbox-player-notes.png";
import img_configText from "./img/sandbox-text-widget.png";
import img_slicerBinding from "./img/sandbox-slicer-binding.png";
import img_slicerWidget from "./img/sandbox-slicer-widget.png";
import img_slicerControl from "./img/sandbox-slicer-control.png";
import img_exportMenu from "./img/sandbox-export-menu.png";
import img_toolbarControls from "./img/sandbox-toolbar-controls.png";

const TUTORIAL_SEEN_KEY = "dashalt_tutorial_seen";

/** True once the user has closed the tutorial in this browser. */
export function hasSeenTutorial(): boolean {
  try {
    return localStorage.getItem(TUTORIAL_SEEN_KEY) === "1";
  } catch {
    return true;
  }
}

export function markTutorialSeen(): void {
  try {
    localStorage.setItem(TUTORIAL_SEEN_KEY, "1");
  } catch {
    // localStorage unavailable. The tutorial opens again on the next visit.
  }
}

interface SandboxTutorialProps {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  /** Mirrors the gate that renders the Sandbox AI button. */
  showSandboxAI?: boolean;
}

interface TutorialImageProps {
  src?: string;
  alt: string;
  name: string;
  height?: number | string;
  width?: number | string;
  maxWidth?: number | string;
}

function TutorialImage({ src, alt, name, height, width, maxWidth }: TutorialImageProps) {
  const style: React.CSSProperties = {};
  if (height != null) style.height = height;
  if (width != null) style.width = width;
  if (maxWidth != null) style.maxWidth = maxWidth;

  if (src) {
    return <img className="sbx-tutorial-image" src={src} alt={alt} style={style} />;
  }
  return (
    <div
      className="sbx-tutorial-image-placeholder"
      role="img"
      aria-label={alt}
      style={style}
    >
      {name}
    </div>
  );
}

function Callout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="sbx-tutorial-callout">
      <div className="sbx-tutorial-callout-title">{title}</div>
      <div className="sbx-tutorial-callout-body">{children}</div>
    </div>
  );
}

// ── Configure Widgets step ────────────────────────────────────

const VARIABLE_EXAMPLES = [
  { token: "{CurrentDate}", meaning: "today's date" },
  { token: "{CurrentMonth}", meaning: "the current month" },
  { token: "{CurrentYear}", meaning: "the current year" },
  { token: "{CurrentMonth - 1}", meaning: "last month" },
  { token: "{CurrentDate + 7}", meaning: "seven days from today" },
  { token: "{CurrentDate:MMMM D, YYYY}", meaning: "today in a custom format" },
];

// A static row: the tutorial has no board, so a slicer token cannot resolve here.
const SLICER_EXAMPLE = {
  token: "{Slicer.WEEK.StartDate:MMM D}",
  meaning: "the first day of the Slicer with key WEEK",
  rendered: "Oct 9",
};

function VariablesNote() {
  return (
    <Callout title="Dynamic variables">
      <p>
        Type a variable in curly braces and the dashboard fills it in each time it loads, so a
        heading like "{"{CurrentMonth}"} Report" stays current. Add + or - to shift it, and a
        colon with format tokens (YYYY, MM, MMMM, D, DD, DDDD) to change how it reads. Use the{" "}
        <b>Variables</b> button, or the <b>?</b> icon next to the title field, to insert one.
        Type {"{Slicer."} to open a menu of the Slicers on the dashboard and quote one's dates
        or athletes by its <b>Key</b>, the short code shown next to its title in the Slicer form.
      </p>
      <ul className="sbx-tutorial-list">
        {VARIABLE_EXAMPLES.map((v) => (
          <li key={v.token}>
            <code>{v.token}</code> shows {v.meaning}: {resolveTemplate(v.token).resolved}
          </li>
        ))}
        <li>
          <code>{SLICER_EXAMPLE.token}</code> shows {SLICER_EXAMPLE.meaning}: {SLICER_EXAMPLE.rendered}
        </li>
      </ul>
    </Callout>
  );
}

const WIDGET_GROUPS: { label: string; types: GridWidgetType[] }[] = [
  { label: "Content", types: ["title", "text", "logo", "spacer"] },
  { label: "Data", types: ["chart", "table", "table_2d", "stat_tile", "gauge", "pie_chart", "radar"] },
  { label: "Athlete", types: ["player_card", "player_notes", "body_map"] },
  { label: "Control", types: ["slicer"] },
];

const WIDGET_HELP: Partial<Record<GridWidgetType, ReactNode>> = {
  title: (
    <>
      <TutorialImage
        src={img_configTitle}
        name="sandbox-title-overview.png"
        alt="Title widget form with the text {CurrentMonth} Overview and a preview that reads September Overview"
        width="100%"
        maxWidth={520}
      />
      <p>Type the heading text and save.</p>
      <VariablesNote />
    </>
  ),
  text: (
    <>
      <TutorialImage
        src={img_configText}
        name="sandbox-text-widget.png"
        alt="Text widget editor with a formatting toolbar, Variables and Preview buttons, and example content that uses date variables"
        width="100%"
        maxWidth={760}
      />
      <p>
        The editor has a toolbar for bold, italic, size, colour, alignment and lists. A new
        Text widget starts with example content. Replace it with your own text. Click{" "}
        <b>Preview</b> to see the variables filled in.
      </p>
      <VariablesNote />
    </>
  ),
  logo: (
    <>
      <p>
        The Logo widget uses the logo from your team settings. There are no fields to fill in.
        Click <b>Add Widget</b> in the modal to place it.
      </p>
    </>
  ),
  spacer: (
    <>
      <p>
        The Spacer has no settings. It appears on the dashboard as soon as you click its card.
        Resize it to push other widgets into place.
      </p>
    </>
  ),
  chart: (
    <>
      <p>
        The configurator opens with one collapsible <b>Plot</b> panel per series. Click <b>Add Plot</b> to add a
        series. Each series has these sections:
      </p>
      <TutorialImage
        src={img_configPlot}
        name="sandbox-config-plot.png"
        alt="Plot configurator with two Plot panels on the left, a live preview on the right, and Group by and Sort chips under the preview"
        width="100%"
        maxWidth={760}
      />
      <ol className="sbx-tutorial-list">
        <li><b>Plot Type</b>: Line or Bar, and the Y-axis side. A bar can stack on an earlier bar.</li>
        <li><b>Select Metrics</b>: one metric from GPS, Testing, Forceplate, Wellness, Nutrition or Cognition, plus Average, Best or Sum. GPS metrics also offer ACWR.</li>
        <li><b>Date Range</b>: a preset, or a custom range with relative or fixed dates. You can keep only some weekdays or only days near a game.</li>
        <li><b>Filters</b>: Athletes (team, players, positions or groups) and Drills. "Exclude Injured Players" is on by default and hides injured athletes' rows.</li>
        <li><b>Additional Options</b>: series name, trendline, data labels, colour and reference lines. Reference lines can be an Average Line or a Custom value.</li>
        <li><b>Group by and Sort by</b>: chips under the preview, not in the form. Group by a time grain or by Player, Position or Drill.</li>
        <li><b>Plot Configuration</b>: the title, the legend, a horizontal layout and "Show all dates in range". The last one draws every day, week or month in the range, empty where there is no data. It does not change the Average Line.</li>
      </ol>
    </>
  ),
  table: (
    <>
      <TutorialImage
        src={img_configTable}
        name="sandbox-config-table.png"
        alt="Table configurator with one column expanded on the left, a table preview on the right, and Group by chips under the preview"
        width="100%"
        maxWidth={760}
      />
      <ul className="sbx-tutorial-list">
        <li><b>Columns</b>: each column is one metric with Average, Best or Sum. You can rename a column, turn on ACWR for GPS metrics and set a font colour.</li>
        <li><b>Group by</b>: chips under the preview, the same as Plots.</li>
        <li><b>Date Range</b>, <b>Players</b> and <b>Exclude Injured Players</b>: the same controls as Plots. The toggle hides injured athletes' rows.</li>
        <li><b>Show Average Row</b> adds a team average at the top.</li>
        <li><b>Separate by Position</b> inserts a bold row per position with that position's averages. It is only available with Group by Player.</li>
      </ul>
      <Callout title="Sort on the dashboard, not in the form">
        Click a column header on the placed widget to sort the table. The sort is saved with
        the dashboard.
      </Callout>
    </>
  ),
  table_2d: (
    <>
      <TutorialImage
        src={img_config2dTable}
        name="sandbox-2d-table.png"
        alt="2D Table configurator with a heat-map preview of players by day, and Rows, Cols and Swap controls under the preview"
        width="100%"
        maxWidth={760}
      />
      <ul className="sbx-tutorial-list">
        <li><b>Dimensions</b>: rows and columns from Player, Position, Drill, Metric, Day, Week, Weekday and Month. Click <b>Swap</b> to flip rows and columns.</li>
        <li>Most pairs must combine an entity with a time grain, or Metric with anything. Three extra pairs are allowed: Player by Drill, Position by Drill, and Week by Weekday.</li>
        <li>When one axis is Metric, you pick one metric per header. Otherwise you pick one metric and an aggregation.</li>
        <li><b>Heatmap Mode</b> colours each cell. <b>Show Data Labels</b> prints the values.</li>
      </ul>
      <Callout title="The axes correct each other">
        If you pick a dimension that cannot pair with the other axis, the other axis changes to
        a valid one. The "Best", "Average" and "Sum" toggles switch between Row and Column
        depending on which axis holds the athletes or weeks.
      </Callout>
    </>
  ),
  stat_tile: (
    <>
      <TutorialImage
        src={img_configStatTile}
        name="sandbox-stat-tile.png"
        alt="Stat Tile configurator with metric, aggregation and comparison on the left, and a preview tile showing the value with team and position figures"
        width="100%"
        maxWidth={760}
      />
      <ul className="sbx-tutorial-list">
        <li><b>Select Metric</b>: one metric and an aggregation.</li>
        <li><b>Comparison</b>: compare the value with another period when the metric supports it.</li>
        <li><b>Date Range</b> and <b>Filters</b>: one athlete, position, group or the full team.</li>
        <li><b>Show Team and Position Data</b> adds the team and position values under the number.</li>
      </ul>
    </>
  ),
  gauge: (
    <>
      <TutorialImage
        src={img_configGauge}
        name="sandbox-gauge-chart.png"
        alt="Gauge configurator with the Filters section open on the left, and a preview dial with red, orange and green segments on the right"
        width="100%"
        maxWidth={760}
      />
      <ul className="sbx-tutorial-list">
        <li><b>Select Metric</b>, <b>Date Range</b> and <b>Filters</b> work as in the Stat Tile.</li>
        <li><b>Scale</b> sets the dial range. Auto scale reads the maximum from your data, so the range can change with the date range. Set a custom Max value to fix it.</li>
        <li><b>Color Segments</b> splits the dial into coloured bands with your own thresholds.</li>
      </ul>
    </>
  ),
  pie_chart: (
    <>
      <TutorialImage
        src={img_configPie}
        name="sandbox-pi-chart.png"
        alt="Pie Chart configurator with three slice panels on the left, a preview pie on the right, and Sort by chips under the preview"
        width="100%"
        maxWidth={760}
      />
      <ul className="sbx-tutorial-list">
        <li>Each slice has its own metric, date range and filters. Add a slice per group you want to compare.</li>
        <li><b>Donut mode</b>, <b>Show data labels</b> and <b>Show percentages</b> change the look.</li>
        <li>Sort chips under the preview order the slices.</li>
      </ul>
    </>
  ),
  radar: (
    <>
      <TutorialImage
        src={img_configRadar}
        name="sandbox-radar-chart.png"
        alt="Radar Chart configurator with the Vertices section open listing metrics on the left, and a five-point radar preview on the right"
        width="100%"
        maxWidth={760}
      />
      <ul className="sbx-tutorial-list">
        <li><b>Dimensions</b>: choose whether the points around the chart are Metrics, Players, Positions or Drills.</li>
        <li><b>Vertices</b>: the items on the points. <b>Series</b>: the lines drawn across them.</li>
        <li><b>Scale</b>: "% Of Max" scales each metric to its best value, "Relative" uses the team spread, "Custom" uses your own Min and Max.</li>
        <li>All series share one <b>Date Range</b>.</li>
      </ul>
    </>
  ),
  player_card: (
    <>
      <p>
        Pick the athlete, then turn the option cards on or off: Name, Headshot, Position,
        Jersey, Top Speed, Body weight Baseline, Recent Avail., Active Injuries and Recent
        Injuries. A Slicer can select the athlete for you.
      </p>
    </>
  ),
  player_notes: (
    <>
      <TutorialImage
        src={img_configPlayerNotes}
        name="sandbox-player-notes.png"
        alt="Player Notes configurator with the Advanced Filters section open on the left, and a preview of three dated notes on the right"
        width="100%"
        maxWidth={760}
      />
      <p>
        Pick a date range and the players. Under <b>Advanced Filters</b>, the{" "}
        <b>Individual Notes</b> tab picks specific notes, and the <b>Filter by Text</b> tab
        searches for text inside the note bodies.
      </p>
    </>
  ),
  body_map: (
    <>
      <TutorialImage
        src={img_configBodyMap}
        name="sandbox-bodymap.png"
        alt="Body Map configurator with Body Map Type, Player and Date sections on the left, and a front and back body heat map preview on the right"
        width="100%"
        maxWidth={760}
      />
      <p>
        Pick the <b>Body Map Type</b>, one athlete and <b>one day</b>. The Body Map does not
        take a date range. The title follows the athlete's name until you edit it.
      </p>
    </>
  ),
  slicer: (
    <>
      <TutorialImage
        src={img_slicerBinding}
        name="sandbox-slicer-binding.png"
        alt="A Plot widget outlined in blue with two coloured discs next to its title, beside a Slicer tile with Athletes and Drills pickers"
        width="100%"
        maxWidth={760}
      />
      <p>
        Give it a title and a colour, then choose one picker: a date range, or athletes and
        drills. Set the values on the tile after you place it. Other widgets follow it when you
        check "Controlled by Slicer" in their Date Range or Filters section. See the{" "}
        <b>Slicer</b> step of this tutorial for the full walkthrough.
      </p>
    </>
  ),
};

interface WidgetMeta {
  label: string;
  description: string;
}

function ConfigureWidgetsStep({
  selected,
  setSelected,
  availableTypes,
  meta,
}: {
  selected: GridWidgetType;
  setSelected: (k: GridWidgetType) => void;
  availableTypes: Set<GridWidgetType>;
  meta: Map<GridWidgetType, WidgetMeta>;
}) {
  const current = meta.get(selected);
  return (
    <div className="sbx-tutorial-prose">
      <p>
        Each widget has its own form on the left and a live preview on the right. Pick a widget
        to see how it is set up.
      </p>

      <div className="sbx-tutorial-tag-groups">
        {WIDGET_GROUPS.map((group) => {
          const types = group.types.filter((t) => availableTypes.has(t));
          if (types.length === 0) return null;
          return (
            <div key={group.label} className="sbx-tutorial-tag-group">
              <span className="sbx-tutorial-tag-group-label">{group.label}</span>
              <Space size={[8, 8]} wrap>
                {types.map((type) => (
                  <Tag.CheckableTag
                    key={type}
                    checked={selected === type}
                    onChange={() => setSelected(type)}
                    className="sbx-widget-tag"
                  >
                    {meta.get(type)?.label ?? type}
                  </Tag.CheckableTag>
                ))}
              </Space>
            </div>
          );
        })}
      </div>

      <div className="sbx-tutorial-widget-help">
        <div className="sbx-tutorial-widget-heading">{current?.label ?? selected}</div>
        {current?.description && (
          <div className="sbx-tutorial-widget-desc">{current.description}</div>
        )}
        {WIDGET_HELP[selected]}
      </div>
      <div className="sbx-tutorial-edit-note">
        <p>
          To change a widget later, hover it and click the <b>⋮</b> menu in its top-right
          corner for <b>Edit</b>, <b>Duplicate</b> or <b>Delete</b>.
        </p>
      </div>
    </div>
  );
}

// ── Steps ─────────────────────────────────────────────────────

type StepKey =
  | "welcome"
  | "add"
  | "configure"
  | "slicer"
  | "export"
  | "controls"
  | "ai"
  | "done";

interface StepDef {
  key: StepKey;
  title: string;
  description: string;
}

export const SandboxTutorial = ({ isOpen, setOpen, showSandboxAI = false }: SandboxTutorialProps) => {
  const widgetAvailability = useSelector(
    ({ team }: any) => team?.info?.sandbox_widget_availability,
  );

  const { availableTypes, meta } = useMemo(() => {
    const available = new Set<GridWidgetType>();
    const metaMap = new Map<GridWidgetType, WidgetMeta>();
    for (const t of getWidgetTemplates()) {
      metaMap.set(t.type, { label: t.label, description: t.description });
      if (!isLocked(t.type, widgetAvailability) && WIDGET_HELP[t.type]) {
        available.add(t.type);
      }
    }
    return { availableTypes: available, meta: metaMap };
  }, [widgetAvailability]);

  const slicerAvailable = availableTypes.has("slicer");

  const steps = useMemo<StepDef[]>(() => {
    const list: StepDef[] = [
      { key: "welcome", title: "Welcome", description: "Build your own dashboards." },
      { key: "add", title: "Add a Widget", description: "Pick a card, drag it into place." },
      { key: "configure", title: "Configure Widgets", description: "Form on the left, preview on the right." },
    ];
    if (slicerAvailable) {
      list.push({ key: "slicer", title: "Slicer", description: "One picker, many widgets." });
    }
    list.push(
      { key: "export", title: "Export and Share", description: "PDF, email and links." },
      { key: "controls", title: "Dashboard Controls", description: "Manage dashboards from the toolbar." },
    );
    if (showSandboxAI) {
      list.push({ key: "ai", title: "Sandbox AI", description: "Describe a change in plain language." });
    }
    list.push({ key: "done", title: "You're All Set", description: "Time to build something." });
    return list;
  }, [slicerAvailable, showSandboxAI]);

  const [current, setCurrent] = useState(0);
  const [selectedWidget, setSelectedWidget] = useState<GridWidgetType>("title");
  const contentRef = useRef<HTMLDivElement>(null);

  // A new step or widget starts at the top of the scrollable content pane.
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [current, selectedWidget]);

  // Keep the selected widget valid when the team's availability map arrives late.
  useEffect(() => {
    if (!availableTypes.has(selectedWidget)) {
      const first = availableTypes.values().next().value;
      if (first) setSelectedWidget(first);
    }
  }, [availableTypes, selectedWidget]);

  const next = () => setCurrent((c) => Math.min(c + 1, steps.length - 1));
  const prev = () => setCurrent((c) => Math.max(c - 1, 0));

  const handleClose = () => {
    markTutorialSeen();
    setOpen(false);
    setCurrent(0);
  };

  const renderContent = (key: StepKey): ReactNode => {
    switch (key) {
      case "welcome":
        return (
          <div className="sbx-tutorial-prose">
            <p>
              The Sandbox lets you build custom dashboards by placing widgets on a grid. There are
              widgets for content (titles, text, logos), data (plots, tables, stat tiles, gauges,
              pies, radars) and athletes (player cards, notes, body maps).
            </p>
            <p>
              A blank dashboard offers three ready-made starting points: <b>GPS Metric
              Overview</b>, <b>Game Day Analysis</b> and <b>7-Day Rolling Summary</b>. The
              template adapts its metrics to your team. You can also start from scratch with{" "}
              <b>Add Widget</b>.
            </p>
            <p>
              Every change saves on its own a moment after you make it. There is no Save button.
              You can keep several dashboards, switch between them, export them to PDF and share
              them by link.
            </p>
            <TutorialImage
              src={img_overview}
              name="sandbox-overview.png"
              alt="A populated Sandbox dashboard with title, logo, charts, and a table"
              width="90%"
            />
          </div>
        );
      case "add":
        return (
          <div className="sbx-tutorial-prose">
            <TutorialImage
              src={img_addBtn}
              name="sandbox-add-widget-button.png"
              alt="The dashboard toolbar with the Add Widget button highlighted in the top-right"
              width="25%"
            />
            <p>
              Click the blue <b>Add Widget</b> button in the top-right of the toolbar to open the
              widget picker. The picker shows one card per widget type. A card with a padlock is not enabled for
              your team. Contact <b>cs@whistleperformance.ai</b> to turn a widget on.
            </p>
            <TutorialImage
              src={img_widgetPicker}
              name="sandbox-widget-picker.png"
              alt="The Add Widget picker with fifteen widget cards; cards not enabled for the team show a padlock"
              width="70%"
            />
            <p>
              Once placed, drag a widget by any part of its tile to move it. Drag the bottom-right
              corner to resize it. Widgets push each other out of the way, and the grid closes
              gaps from the top.
            </p>
          </div>
        );
      case "configure":
        return (
          <ConfigureWidgetsStep
            selected={selectedWidget}
            setSelected={setSelectedWidget}
            availableTypes={availableTypes}
            meta={meta}
          />
        );
      case "slicer":
        return (
          <div className="sbx-tutorial-prose">
            <p>
              A <b>Slicer</b> is a picker on the dashboard. Widgets that follow it use its date
              range or its athletes instead of their own. Set it up in this order:
            </p>
            <ol className="sbx-tutorial-list">
              <li><b>Add the Slicer first.</b> The "Controlled by Slicer" checkbox in other widgets stays disabled until a Slicer exists.</li>
              <li>Give it a title and a colour. Choose one picker: <b>Date range</b>, or <b>Athletes &amp; drills</b>.</li>
              <li>Place it, then set the dates or athletes on the tile itself.</li>
              <li>Edit a widget. Open its <b>Date Range</b> or <b>Filters</b> section and check <b>Controlled by Slicer</b>. Pick the Slicer by name.</li>
              <li>The widget now shows a disc next to its title in the Slicer's colour. Hover the Slicer to outline every widget that follows it.</li>
            </ol>
            <TutorialImage
              src={img_slicerControl}
              name="sandbox-slicer-control.png"
              alt="A widget's Filters section with Controlled by Slicer checked and Slicer 2 selected; the Athletes and Drills fields are greyed out and show the Slicer's values"
              width="100%"
              maxWidth={460}
            />
            <TutorialImage
              src={img_slicerWidget}
              name="sandbox-slicer-widget.png"
              alt="A Plot widget with an orange and a blue disc next to its title, beside a blue Athletes and Drills Slicer and an orange Date range Slicer"
              width="90%"
            />
            <Callout title="Good to know">
              <ul className="sbx-tutorial-list">
                <li>A bound section goes grey and shows the Slicer's values. Your own values are kept, and come back when you uncheck the box.</li>
                <li>Plots and Pie Charts bind per series. Check each series you want to follow the Slicer.</li>
                <li>A Body Map uses the last day of the Slicer's date range. A Player Card uses the first athlete.</li>
                <li>The option "[Underlying Positions]" makes the widget follow the positions of the picked athletes instead of the athletes.</li>
                <li>You cannot delete a Slicer while widgets follow it or a Title or Text widget quotes its Key. Remove their bindings or tokens first.</li>
                <li>A change on the tile applies when the dropdown closes, or after a short pause.</li>
              </ul>
            </Callout>
          </div>
        );
      case "export":
        return (
          <div className="sbx-tutorial-prose">
            <p>
              Click <b>Export PDF</b> in the toolbar. The dropdown has three options:
            </p>
            <TutorialImage
              src={img_exportMenu}
              name="sandbox-export-menu.png"
              alt="The Export PDF dropdown open with Export Only, Annotate + Export and Send Email"
              width="35%"
            />
            <ul className="sbx-tutorial-list">
              <li><b>Export Only</b> downloads the dashboard as a landscape PDF that matches the screen.</li>
              <li><b>Annotate + Export</b> opens the PDF in the annotator first, so you can mark it up before you download.</li>
              <li><b>Send Email</b> asks for a subject, a message and recipients. You can pick yourself and the team's coaches.</li>
            </ul>
            <p>
              Dashed lines labelled "Page 1", "Page 2" and so on show where the PDF splits. Move
              widgets so no widget sits across a line. The email option waits until the dashboard
              has loaded its data, and warns you when some widgets are still empty.
            </p>
            <p>
              Click <b>Copy URL</b> to copy a link to the current dashboard. Anyone on your team
              with Sandbox access can open it.
            </p>
          </div>
        );
      case "controls":
        return (
          <div className="sbx-tutorial-prose">
            <p>
              Use the dropdown on the left of the toolbar to switch between your dashboards.
              Five icon buttons beside it manage the current dashboard:
            </p>
            <TutorialImage
              src={img_toolbarControls}
              name="sandbox-toolbar-controls.png"
              alt="Dashboard dropdown with five icon buttons beside it: rename, new, reload, duplicate, delete"
              width="40%"
            />
            <ul className="sbx-tutorial-list">
              <li><b>Rename</b> changes the dashboard's name.</li>
              <li><b>New</b> creates a blank dashboard.</li>
              <li><b>Reload</b> fetches the dashboard and its data again from the server.</li>
              <li><b>Duplicate</b> makes a copy of the current dashboard.</li>
              <li><b>Delete</b> removes the current dashboard. This cannot be undone.</li>
            </ul>
            <p>
              A greyed button means you do not have that permission. Hover it to see which one.
              Ask your team's permissions manager to grant it.
            </p>
            <p>
              Changes save on their own about a second after you stop editing. If a save fails you
              see a "Save failed" notice, and the next change tries again. The <b>?</b> button
              opens help videos for this page.
            </p>
          </div>
        );
      case "ai":
        return (
          <div className="sbx-tutorial-prose">
            <p>
              The round <b>Sandbox AI</b> button in the bottom-right corner opens a chat panel.
              Describe a change in plain language and it builds, edits or rearranges the
              dashboard for you. For example:
            </p>
            <ul className="sbx-tutorial-list">
              <li>"Build me a weekly training load dashboard"</li>
              <li>"Add a bar chart of total distance by player for last week"</li>
              <li>"Move the tables side by side under the title"</li>
            </ul>
            <p>
              After each reply the changes apply to the dashboard at once. A bar above the input
              says how many changes were made, with <b>Undo</b> and <b>Keep</b>. Undo reverts the
              latest reply only. Sending the next message keeps the changes.
            </p>
            <p>
              While it works, the send button becomes <b>Cancel</b>. On an unnamed dashboard the
              assistant also picks a name.
            </p>
          </div>
        );
      case "done":
        return (
          <div className="sbx-tutorial-prose">
            <p>
              You're ready to build dashboards in the Sandbox. Open this tutorial again at any
              time with the <b>Show Tutorial</b> button in the toolbar.
            </p>
            <p>
              Need help? Reach out to our support team anytime at{" "}
              <b>cs@whistleperformance.ai</b>.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  const safeCurrent = Math.min(current, steps.length - 1);
  const isLast = safeCurrent === steps.length - 1;
  const step = steps[safeCurrent];

  return (
    <Modal
      className="sbx-tutorial-modal"
      title="Sandbox Tutorial"
      open={isOpen}
      onCancel={handleClose}
      width={1200}
      centered
      footer={
        <Space>
          {safeCurrent > 0 && <Button onClick={prev}>Previous</Button>}
          {isLast ? (
            <Button type="primary" onClick={handleClose}>Done</Button>
          ) : (
            <Button type="primary" onClick={next}>Next</Button>
          )}
        </Space>
      }
    >
      <div className="sbx-tutorial-body">
        <div className="sbx-tutorial-steps">
          <Steps
            current={safeCurrent}
            onChange={setCurrent}
            items={steps.map(({ title, description }) => ({ title, description }))}
            direction="vertical"
            size="small"
          />
        </div>

        <div className="sbx-tutorial-content" ref={contentRef}>
          <Typography.Title level={3}>{step.title}</Typography.Title>
          {renderContent(step.key)}
        </div>
      </div>
    </Modal>
  );
};
